import { z } from 'zod'
import { DateRangeFilterSchema } from '../time'
import { LatLngSchema } from '../locations/latLng'
import {
  AccountIdSchema,
  LocationIdSchema,
  UserIdSchema,
  WorkflowActionIdSchema,
  WorkflowIdSchema,
  WorkflowStageIdSchema,
} from '../ids'
import { PaginatedCursorInputSchema, PaginatedOutputSchema } from '../elasticApi'
import { apiRequest, apiResponses, ResponseErrorSchema } from '../api'
import {
  ConversationDataSchema,
  ConversationFilterSchema,
  ConversationIdSchema,
  ConversationSortBySchema,
  ConversationSortOrderSchema,
} from './model'

/**
 * /conversation/get
 */
export const ConversationGetInputSchema = apiRequest({
  conversationId: ConversationIdSchema,
})
export const [ConversationGetSuccessSchema, ConversationGetOutputSchema] = apiResponses({
  conversation: ConversationDataSchema,
})
export type ConversationGetInput = z.infer<typeof ConversationGetInputSchema>
export type ConversationGetSuccess = z.infer<typeof ConversationGetSuccessSchema>
export type ConversationGetOutput = z.infer<typeof ConversationGetOutputSchema>

/**
 * /conversation/create
 */
export const ConversationCreateInputSchema = apiRequest({
  industry: z.string().nonempty().min(3),
  quality: z.union([z.literal('good'), z.literal('bad'), z.literal('average')]),
  latlng: LatLngSchema.optional(),
})
export const [ConversationCreateSuccessSchema, ConversationCreateOutputSchema] = apiResponses({})
export type ConversationCreateInput = z.infer<typeof ConversationCreateInputSchema>
export type ConversationCreateSuccess = z.infer<typeof ConversationCreateSuccessSchema>
export type ConversationCreateOutput = z.infer<typeof ConversationCreateOutputSchema>

/**
 * /conversation/list
 */
export const ConversationListInputSchema = apiRequest({
  reviewFlagged: z
    .boolean()
    .optional()
    .describe(
      'Review all flagged conversations in the current company. Requires company-level conversation:manage. ' +
        'Total is omitted because eligibility is rechecked after hydration; paginate using sortTimestamp, even after an empty page.'
    ),
  userId: UserIdSchema.optional(),
  showAssigned: z.boolean().optional(),
  workflow: z
    .object({
      workflowId: WorkflowIdSchema.optional().meta({ deprecated: true }).describe('not needed anymore'),
      actionOrStageIds: z.union([z.array(WorkflowActionIdSchema), z.array(WorkflowStageIdSchema)]).optional(),
    })
    .optional(),
  date: DateRangeFilterSchema.optional(),
  ...PaginatedCursorInputSchema.shape,
  sortBy: ConversationSortBySchema.optional().meta({
    description: 'Field to sort conversations by. Defaults to date.',
  }),
  sortOrder: ConversationSortOrderSchema.optional().meta({ description: 'Sort direction. Defaults to desc.' }),
  filter: ConversationFilterSchema.optional(),
})
export const [ConversationListSuccessSchema, ConversationListOutputSchema] = apiResponses({
  conversations: z.array(ConversationDataSchema),
  ...PaginatedOutputSchema.shape,
})
export type ConversationListInput = z.infer<typeof ConversationListInputSchema>
export type ConversationListSuccess = z.infer<typeof ConversationListSuccessSchema>
export type ConversationListOutput = z.infer<typeof ConversationListOutputSchema>

/**
 * /conversation/categories
 */
export const ConversationCategoriesInputSchema = apiRequest({})
export const [ConversationCategoriesSuccessSchema, ConversationCategoriesOutputSchema] = apiResponses({
  categories: z.array(z.object({ name: z.string(), count: z.number() })),
})
export type ConversationCategoriesInput = z.infer<typeof ConversationCategoriesInputSchema>
export type ConversationCategoriesSuccess = z.infer<typeof ConversationCategoriesSuccessSchema>
export type ConversationCategoriesOutput = z.infer<typeof ConversationCategoriesOutputSchema>

/**
 * /conversation/shareable
 */
export const ConversationShareableInputSchema = apiRequest({
  category: z.string().nonempty(),
})
export const [ConversationShareableSuccessSchema, ConversationShareableOutputSchema] = apiResponses({
  conversations: z.array(ConversationDataSchema),
})
export type ConversationShareableInput = z.infer<typeof ConversationShareableInputSchema>
export type ConversationShareableSuccess = z.infer<typeof ConversationShareableSuccessSchema>
export type ConversationShareableOutput = z.infer<typeof ConversationShareableOutputSchema>

/**
 * /conversation/update
 */
export const ConversationUpdateInputSchema = apiRequest({
  conversation: ConversationDataSchema.pick({
    convoId: true,
    assigneeId: true,
    skillShare: true,
    aiSkillShare: true,
    locationId: true,
    rubric: true,
    accountId: true,
  }),
})
export const [ConversationUpdateSuccessSchema, ConversationUpdateOutputSchema] = apiResponses({
  conversation: ConversationDataSchema,
})
export type ConversationUpdateInput = z.infer<typeof ConversationUpdateInputSchema>
export type ConversationUpdateSuccess = z.infer<typeof ConversationUpdateSuccessSchema>
export type ConversationUpdateOutput = z.infer<typeof ConversationUpdateOutputSchema>

/**
 * /conversation/remove
 * Flagged recordings require company-level conversation:manage, not owner-level permission.
 * Deletes the conversation and stored playback audio. When a temporary source key is retained, the source
 * is deleted first to prevent later event replays from recreating the conversation, regardless of status.
 * Legacy Flagged rows without that key still succeed and log skipped source cleanup; replay remains possible
 * until the source expires under RecordingSegmentTempBucket's lifecycle (apps/infra/src/skill/recording/bucket.ts).
 */
export const ConversationRemoveInputSchema = apiRequest({
  conversationId: ConversationIdSchema,
})
export const [ConversationRemoveSuccessSchema, ConversationRemoveOutputSchema] = apiResponses({})
export type ConversationRemoveInput = z.infer<typeof ConversationRemoveInputSchema>
export type ConversationRemoveSuccess = z.infer<typeof ConversationRemoveSuccessSchema>
export type ConversationRemoveOutput = z.infer<typeof ConversationRemoveOutputSchema>

/**
 * /conversation/upload
 */
export const ConversationUploadInputSchema = apiRequest({
  accountId: AccountIdSchema.optional(),
  locationId: LocationIdSchema.optional(),
  contentType: z.string().startsWith('audio/'),
  contentLength: z
    .int()
    .min(1)
    .max(100 * 1024 * 1024), // max 100MB
})
export const [ConversationUploadSuccessSchema, ConversationUploadOutputSchema] = apiResponses({
  uploadUrl: z.string(),
})
export type ConversationUploadInput = z.infer<typeof ConversationUploadInputSchema>
export type ConversationUploadSuccess = z.infer<typeof ConversationUploadSuccessSchema>
export type ConversationUploadOutput = z.infer<typeof ConversationUploadOutputSchema>

/**
 * /conversation/regrade
 */
export const ConversationRegradeInputSchema = apiRequest({
  conversationIds: ConversationIdSchema.array().min(1).max(100),
})
export const [ConversationRegradeSuccessSchema, ConversationRegradeOutputSchema] = apiResponses({
  acknowledged: z
    .array(ConversationIdSchema)
    .describe('IDs of non-flagged conversations that were found and the user had permission to regrade'),
  rejected: z
    .array(ConversationIdSchema)
    .describe('IDs of conversations that were found but were flagged or the user lacked permission to regrade'),
  notFound: z.array(ConversationIdSchema).describe('IDs of conversations that were not found'),
})
export type ConversationRegradeInput = z.infer<typeof ConversationRegradeInputSchema>
export type ConversationRegradeSuccess = z.infer<typeof ConversationRegradeSuccessSchema>
export type ConversationRegradeOutput = z.infer<typeof ConversationRegradeOutputSchema>

/**
 * /conversation/resolve
 * Requires company-level conversation:manage; authorized requests for non-Flagged conversations return Conflict.
 * Discard uses /conversation/remove, including its legacy temporary-source cleanup exception.
 * Relocate success only acknowledges queued work, not completed processing; the conversation ID stays unchanged.
 * The worker detaches the previous account and location, clears flag violations, and resumes AI processing
 * at the selected location. Once that claim succeeds it deletes the temporary source so a late source-event replay
 * cannot start a second conversation. Flagged playback restrictions remain until stored audio has been redacted.
 * A destination with a customer account in a recording-flag stage returns LocationFlagged with matches.
 */
export const ConversationResolveInputSchema = apiRequest({
  conversationId: ConversationIdSchema,
  resolution: z.discriminatedUnion('action', [
    z.object({ action: z.literal('discard') }),
    z.object({ action: z.literal('relocate'), locationId: LocationIdSchema }),
  ]),
})
export const [ConversationResolveSuccessSchema] = apiResponses({})
export const ConversationResolveLocationFlaggedSchema = z.object({
  ...ResponseErrorSchema.shape,
  error: z.literal('LocationFlagged'),
  matches: z.array(
    z.object({
      accountId: AccountIdSchema,
      workflowStageId: WorkflowStageIdSchema,
      locationId: LocationIdSchema,
    })
  ),
})
export const ConversationResolveOutputSchema = z.union([
  ConversationResolveSuccessSchema,
  ConversationResolveLocationFlaggedSchema,
  ResponseErrorSchema,
])
export type ConversationResolveInput = z.infer<typeof ConversationResolveInputSchema>
export type ConversationResolution = z.infer<typeof ConversationResolveInputSchema.shape.resolution>
export type ConversationResolveSuccess = z.infer<typeof ConversationResolveSuccessSchema>
export type ConversationResolveLocationFlagged = z.infer<typeof ConversationResolveLocationFlaggedSchema>
export type ConversationResolveOutput = z.infer<typeof ConversationResolveOutputSchema>
